import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { applyConsent, readStoredConsent, storeConsent, type ConsentChoice } from "@/lib/analytics";
import { useT } from "@/i18n";

/** Cookie consent popup: analytics/ad cookies stay off until the visitor accepts. */
export function CookieConsent() {
  const { t } = useT();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (readStoredConsent() === null) setVisible(true);
  }, []);

  function choose(choice: ConsentChoice) {
    storeConsent(choice);
    applyConsent(choice);
    window.dispatchEvent(new CustomEvent("behot:consent", { detail: choice }));
    setVisible(false);
  }

  return (
    <Dialog open={visible} onOpenChange={(open) => !open && choose("denied")}>
      <DialogContent
        className="sm:max-w-sm p-4 gap-3"
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader className="gap-1">
          <DialogTitle className="text-base">{t("cookie.title")}</DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            {t("shell.cookie.description")}{" "}
            <Link to="/cookie" className="font-medium text-primary underline">
              {t("footer.cookie")}
            </Link>
            .
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <div className="flex items-start gap-2.5 rounded-lg border p-2.5">
            <div className="mt-0.5 h-3 w-3 rounded-full bg-green-500" />
            <div>
              <p className="text-xs font-medium">{t("shell.cookie.necessary")}</p>
              <p className="text-[11px] text-muted-foreground">{t("shell.cookie.necessaryDesc")}</p>
            </div>
          </div>
          <div className="flex items-start gap-2.5 rounded-lg border border-dashed p-2.5">
            <div className="mt-0.5 h-3 w-3 rounded-full bg-muted" />
            <div>
              <p className="text-xs font-medium">{t("shell.cookie.analytics")}</p>
              <p className="text-[11px] text-muted-foreground">{t("shell.cookie.analyticsDesc")}</p>
            </div>
          </div>
        </div>

        <DialogFooter className="flex-col-reverse gap-2 sm:flex-col-reverse">
          <Button variant="outline" size="sm" className="w-full" onClick={() => choose("denied")}>
            {t("cookie.reject")}
          </Button>
          <Button variant="hot" size="sm" className="w-full" onClick={() => choose("granted")}>
            {t("cookie.accept")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Small link that reopens the choice (used in the footer). */
export function CookieSettingsButton({ className }: { className?: string }) {
  const { t } = useT();
  return (
    <button
      type="button"
      className={className}
      onClick={() => {
        try {
          window.localStorage.removeItem("behot_cookie_consent");
        } catch {
          /* ignore */
        }
        window.location.reload();
      }}
    >
      {t("cookie.settings")}
    </button>
  );
}
